import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2 } from "lucide-react";

// Total-score levels (instrument.severity_levels); the matching level code picks the character card.
export default function SeverityLevelsEditor({ levels, onChange, lang }) {
  const upd = (i, patch) => onChange(levels.map((l, k) => k === i ? { ...l, ...patch } : l));
  const rm = (i) => onChange(levels.filter((_, k) => k !== i));
  const add = () => onChange([
    ...levels,
    { code: `level_${levels.length + 1}`, label_id: "", label_en: "", min_score: null, max_score: null, min_percentage: null, max_percentage: null, },
  ]);
  return (
    <div className="p-6 rounded-2xl border border-border bg-card space-y-4" data-testid="rt-severity-block">
      <div>
        <div className="font-display text-lg">{lang === "id" ? "Tingkat / Severity Levels" : "Severity Levels"}</div>
        <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
          {lang === "id"
            ? "Tetapkan tingkat berdasarkan skor total (min/max) atau persentase (0-100). Contoh GAD-7: Minimal 0-4, Ringan 5-9, Sedang 10-14, Berat 15-21. Gambar dan refleksi tiap tingkat diisi di character card pada tab Hasil, dengan key sama dengan kode tingkat."
            : "Define levels by total score (min/max) or percentage (0-100). Example GAD-7: Minimal 0-4, Mild 5-9, Moderate 10-14, Severe 15-21. The image and reflection for each level go in the character card on the Result tab, keyed by the level code."}
        </p>
      </div>
      <div className="space-y-3">
        {levels.map((l, i) => (
          <div key={i} className="p-4 rounded-xl border border-border bg-secondary/30 space-y-3" data-testid={`rt-sev-row-${i}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground w-16">Code</Label>
              <Input value={l.code || ""} onChange={(e) => upd(i, { code: e.target.value })} className="h-8 max-w-xs font-mono text-xs" data-testid={`rt-sev-code-${i}`} />
              <Button size="icon" variant="ghost" onClick={() => rm(i)} className="text-destructive ml-auto" data-testid={`rt-sev-rm-${i}`}><Trash2 className="w-4 h-4" /></Button>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "id" ? "Nama Tingkat (ID)" : "Level Name (ID)"}</Label>
                <Input value={l.label_id || ""} onChange={(e) => upd(i, { label_id: e.target.value })} placeholder={lang === "id" ? "cth: Ringan" : "e.g. Mild"} data-testid={`rt-sev-label-id-${i}`} />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "id" ? "Nama Tingkat (EN)" : "Level Name (EN)"}</Label>
                <Input value={l.label_en || ""} onChange={(e) => upd(i, { label_en: e.target.value })} placeholder="e.g. Mild" data-testid={`rt-sev-label-en-${i}`} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Min score</Label>
                  <Input type="number" value={l.min_score ?? ""} onChange={(e) => upd(i, { min_score: e.target.value === "" ? null : Number(e.target.value) })} className="h-9" data-testid={`rt-sev-min-${i}`} />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Max score</Label>
                  <Input type="number" value={l.max_score ?? ""} onChange={(e) => upd(i, { max_score: e.target.value === "" ? null : Number(e.target.value) })} className="h-9" data-testid={`rt-sev-max-${i}`} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Min %</Label>
                  <Input type="number" min={0} max={100} value={l.min_percentage ?? ""} onChange={(e) => upd(i, { min_percentage: e.target.value === "" ? null : Number(e.target.value) })} className="h-9" placeholder="opt" data-testid={`rt-sev-minpct-${i}`} />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Max %</Label>
                  <Input type="number" min={0} max={100} value={l.max_percentage ?? ""} onChange={(e) => upd(i, { max_percentage: e.target.value === "" ? null : Number(e.target.value) })} className="h-9" placeholder="opt" data-testid={`rt-sev-maxpct-${i}`} />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <Button onClick={add} variant="outline" className="rounded-full" data-testid="rt-sev-add"><Plus className="w-4 h-4 mr-1" /> {lang === "id" ? "Tambah Tingkat" : "Add Level"}</Button>
    </div>
  );
}
